import { useState } from "react";
import {
  createFileRoute,
  Link,
  useNavigate,
  useRouter,
  redirect,
} from "@tanstack/react-router";
import { z } from "zod";
import { toast } from "sonner";
import { ShieldCheck, UserRound, Lock, Eye, EyeOff, Mail, Phone, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { notifyOnSignup } from "@/lib/api/signup-notify.functions";
import { recordAuthSecurityEvent } from "@/lib/api/security-alerts.functions";

const ADMIN_EMAIL = "nkadimengdanny7@gmail.com";

const searchSchema = z.object({
  redirect: z.string().optional(),
  mode: z.enum(["signin", "signup"]).optional(),
  role: z.enum(["client", "admin"]).optional(),
  portal: z.enum(["lxp", "ndlcs"]).optional(),
});

export const Route = createFileRoute("/auth")({
  validateSearch: searchSchema,
  beforeLoad: async ({ search }) => {
    if (typeof window === "undefined") return;
    const { data } = await supabase.auth.getUser();
    if (data.user) {
      const fallback = search.portal === "lxp" ? "/lexpractiq/dashboard" : "/portal";
      throw redirect({ to: (search.redirect as any) || fallback });
    }
  },
  head: () => ({
    meta: [
      { title: "Client & admin portal sign in | Nkadimeng Danny Legal & Compliance" },
      {
        name: "description",
        content:
          "Sign in to the Nkadimeng Danny portal. Clients can track cases, uploads and payments. Admin sign-in is restricted to the practice owner.",
      },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const [role, setRole] = useState<"client" | "admin">(search.role ?? "client");
  const [mode, setMode] = useState<"signin" | "signup">(search.mode ?? "signin");
  const [email, setEmail] = useState(search.role === "admin" ? ADMIN_EMAIL : "");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);

  const isAdmin = role === "admin";
  const isLxp = search.portal === "lxp";
  const clientDefault = isLxp ? "/lexpractiq/dashboard" : "/portal";
  const dest = (search.redirect as string) || (isAdmin ? "/portal/admin/documents" : clientDefault);

  function switchRole(next: "client" | "admin") {
    setRole(next);
    if (next === "admin") {
      setMode("signin");
      setEmail(ADMIN_EMAIL);
    } else {
      if (email === ADMIN_EMAIL) setEmail("");
    }
  }

  async function onGoogle() {
    if (isAdmin) {
      toast.error("Admin sign-in requires email & password.");
      return;
    }
    setLoading(true);
    const res = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin + dest,
    });
    if (res.error) {
      toast.error("Google sign-in failed", { description: String(res.error?.message ?? res.error) });
      setLoading(false);
      return;
    }
    if (res.redirected) return;
    await router.invalidate();
    navigate({ to: dest as any });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      if (isAdmin && email.trim().toLowerCase() !== ADMIN_EMAIL) {
        throw new Error("This email is not authorised for admin access.");
      }
      if (mode === "signup") {
        const { data: signUpData, error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            emailRedirectTo: window.location.origin + dest,
            data: { full_name: fullName, phone },
          },
        });
        if (error) throw error;
        // Analytics: mark a completed signup for funnel measurement.
        try {
          const { trackSignupEvent } = await import("@/lib/signup-analytics");
          trackSignupEvent("signup_completed", "auth_form", {
            user_id: signUpData?.user?.id,
          });
        } catch {}
        // Fire-and-forget welcome email + SMS notifications (admin + user)
        notifyOnSignup({
          data: {
            email: email.trim(),
            full_name: fullName || email.trim(),
            phone: phone || "",
          },
        }).catch((e) => console.warn("signup notify failed", e));
        // Flag so the very next client sign-in routes straight to the
        // credits top-up page (R30 minimum via PayFast).
        try {
          window.localStorage.setItem("ndlcs:pending-first-topup", "1");
        } catch {}
        toast.success("Account created", {
          description:
            "Check your email to confirm, then sign in. You'll be taken straight to top up your credits (R30 minimum).",
        });
        setMode("signin");
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        if (error) {
          const emailLower = email.trim().toLowerCase();
          const isAdminAttempt = emailLower === ADMIN_EMAIL.toLowerCase();
          const attempts = failedAttempts + 1;
          setFailedAttempts(attempts);
          // Only raise a security alert for admin attempts, or for a real
          // burst of client failures. A client who simply hasn't set a
          // password yet must never trigger an admin security alert.
          if (isAdminAttempt || attempts >= 4) {
            void recordAuthSecurityEvent({
              data: {
                eventType: isAdminAttempt ? "admin_signin_failed" : "client_signin_failed_burst",
                email: emailLower,
                metadata: { reason: error.message, role, attempts },
              },
            }).catch(() => {});
          }
          if (!isAdminAttempt && /invalid login credentials/i.test(error.message)) {
            throw new Error(
              "That email and password don't match. If we created your account for you, you haven't set a password yet — tap \u201cEmail me a sign-in link\u201d below and you'll go straight in.",
            );
          }
          throw error;
        }
        setFailedAttempts(0);

        if (isAdmin) {
          void recordAuthSecurityEvent({
            data: {
              eventType: "admin_signin_success",
              email: email.trim().toLowerCase(),
              metadata: { role },
            },
          }).catch(() => {});
        }
        toast.success(isAdmin ? "Welcome back, Counsel" : "Welcome back");
        await router.invalidate();
        // Brand-new client? Send them to the credits top-up page so they
        // can instantly add credits via PayFast.
        let nextDest: string = dest;
        if (!isAdmin && !search.redirect && !isLxp) {
          try {
            if (window.localStorage.getItem("ndlcs:pending-first-topup") === "1") {
              window.localStorage.removeItem("ndlcs:pending-first-topup");
              nextDest = "/credits?welcome=1";
            }
          } catch {}
        }
        navigate({ to: nextDest as any });
      }
    } catch (err: any) {
      toast.error(mode === "signup" ? "Sign-up failed" : "Sign-in failed", {
        description: err?.message ?? String(err),
      });
    } finally {
      setLoading(false);
    }
  }

  async function onForgotPassword() {
    if (!email.trim()) {
      toast.error("Enter your email above first, then tap Forgot password.");
      return;
    }
    // Admin password recovery is deliberately disabled — the owner account
    // must not be resettable from a public form.
    if (isAdmin || email.trim().toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
      toast.error("Password reset is not available for the admin account.");
      void recordAuthSecurityEvent({
        data: {
          eventType: "admin_password_reset_blocked",
          email: email.trim().toLowerCase(),
          metadata: { role },
        },
      }).catch(() => {});
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: window.location.origin + "/reset-password",
    });
    if (error) {
      toast.error("Couldn't send reset link", { description: error.message });
      return;
    }
    toast.success("Reset link sent", {
      description: "Check your inbox (and spam) for the password reset link.",
    });
  }


  /** Passwordless sign-in for accounts we created on the client's behalf. */
  async function onMagicLink() {
    const addr = email.trim();
    if (!addr) {
      toast.error("Enter your email above first, then tap the sign-in link button.");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithOtp({
      email: addr,
      options: {
        shouldCreateUser: false,
        emailRedirectTo: window.location.origin + "/portal/dashboard",
      },
    });
    setLoading(false);
    if (error) {
      toast.error("Couldn't send your sign-in link", { description: error.message });
      return;
    }
    toast.success("Sign-in link sent", {
      description:
        "Open the link in your email (check spam too) and you'll land straight on your dashboard.",
    });
  }


  return (
    <div className="container-narrow grid min-h-[80vh] place-items-center px-4 py-10 sm:py-16">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-soft sm:p-8">
        <p className="text-[11px] uppercase tracking-[0.3em] text-gold">
          {isAdmin ? "Practice console" : "Client portal"}
        </p>
        <h1 className="mt-2 font-display text-2xl text-navy sm:text-3xl">
          {isAdmin
            ? "Admin sign-in"
            : mode === "signin"
            ? "Welcome back"
            : "Create your account"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {isAdmin
            ? "Restricted to the practice owner. All sign-ins are logged for POPIA & LPC compliance."
            : mode === "signin"
            ? "Sign in to track your case, uploads and payments."
            : "It only takes a minute — your details are encrypted and protected under POPIA."}
        </p>

        <div className="mt-5 grid grid-cols-2 gap-2 rounded-lg border border-border bg-muted/40 p-1">
          <button
            type="button"
            onClick={() => switchRole("client")}
            className={cn(
              "flex items-center justify-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium transition",
              !isAdmin
                ? "bg-background text-navy shadow-sm"
                : "text-muted-foreground hover:text-navy",
            )}
          >
            <UserRound className="h-4 w-4" /> Client
          </button>
          <button
            type="button"
            onClick={() => switchRole("admin")}
            className={cn(
              "flex items-center justify-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium transition",
              isAdmin
                ? "bg-navy text-navy-foreground shadow-sm"
                : "text-muted-foreground hover:text-navy",
            )}
          >
            <ShieldCheck className="h-4 w-4" /> Admin
          </button>
        </div>

        {!isAdmin && (
          <div className="mt-4 grid grid-cols-2 gap-1 rounded-lg border border-border bg-muted/30 p-1 sm:hidden">
            <button
              type="button"
              onClick={() => setMode("signin")}
              className={cn(
                "rounded-md px-3 py-2 text-sm font-medium transition",
                mode === "signin" ? "bg-background text-navy shadow-sm" : "text-muted-foreground",
              )}
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={() => setMode("signup")}
              className={cn(
                "rounded-md px-3 py-2 text-sm font-medium transition",
                mode === "signup" ? "bg-background text-navy shadow-sm" : "text-muted-foreground",
              )}
            >
              Create account
            </button>
          </div>
        )}

        {isAdmin ? (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-navy/15 bg-navy/5 p-3 text-xs text-navy/80">
            <Lock className="mt-0.5 h-4 w-4 text-navy" />
            <span>
              Admin access is bound to <strong>{ADMIN_EMAIL}</strong>. Unauthorised
              attempts are recorded. This is a paralegal &amp; compliance practice — not
              an admitted attorney.
            </span>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            className="mt-6 h-11 w-full"
            onClick={onGoogle}
            disabled={loading}
          >
            Continue with Google
          </Button>
        )}

        {!isAdmin && (
          <div className="relative my-6 text-center text-xs uppercase tracking-widest text-muted-foreground">
            <span className="relative z-10 bg-card px-2">or with email</span>
            <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
          </div>
        )}

        <form onSubmit={onSubmit} className={cn("space-y-3", isAdmin && "mt-5")}>
          {!isAdmin && mode === "signup" && (
            <>
              <div>
                <Label htmlFor="fullName">Full name</Label>
                <Input
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  minLength={2}
                  autoComplete="name"
                  className="h-11"
                  placeholder="Thandi Mokoena"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone (optional)</Label>
                <div className="relative">
                  <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="phone"
                    type="tel"
                    inputMode="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="071 276 6198"
                    autoComplete="tel"
                    className="h-11 pl-9"
                  />
                </div>
              </div>
            </>
          )}
          <div>
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                readOnly={isAdmin}
                placeholder="you@example.com"
                className={cn("h-11 pl-9", isAdmin && "bg-muted/60")}
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              {!isAdmin && mode === "signin" && (
                <button
                  type="button"
                  onClick={onForgotPassword}
                  className="text-xs font-medium text-navy hover:underline"
                >
                  Forgot password?
                </button>
              )}
            </div>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                placeholder={mode === "signup" ? "At least 6 characters" : "Your password"}
                className="h-11 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground hover:text-navy"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {!isAdmin && mode === "signup" && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Use 6+ characters. A mix of letters, numbers and a symbol is strongest.
              </p>
            )}
          </div>
          <Button
            type="submit"
            className="h-11 w-full bg-navy text-base hover:bg-navy/90"
            disabled={loading}
          >
            {loading
              ? "Please wait…"
              : isAdmin
              ? "Enter admin console"
              : mode === "signin"
              ? "Sign in"
              : "Create my account"}
          </Button>
        </form>

        {!isAdmin && mode === "signin" && (
          <div className="mt-4 rounded-lg border border-border bg-muted/30 p-3">
            <p className="text-xs text-muted-foreground">
              Did we create your account for you (after we emailed you about your CV, ATS
              scan or matter)? You don&rsquo;t have a password yet — get a one-tap link
              instead.
            </p>
            <button
              type="button"
              onClick={onMagicLink}
              disabled={loading}
              className="mt-2 w-full rounded-md border border-navy/30 px-3 py-2 text-sm font-semibold text-navy hover:bg-navy/5 disabled:opacity-60"
            >
              Email me a sign-in link
            </button>
          </div>
        )}



        {!isAdmin && (
          <>
            <p className="mt-6 text-center text-sm text-muted-foreground">
              {mode === "signin" ? "New here?" : "Already have an account?"}{" "}
              <button
                type="button"
                className="font-medium text-navy underline-offset-2 hover:underline"
                onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
              >
                {mode === "signin" ? "Create an account" : "Sign in instead"}
              </button>
            </p>
            {mode === "signup" && (
              <ul className="mt-5 space-y-1.5 rounded-md bg-muted/40 p-3 text-[12px] text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 text-gold" />
                  Track every matter, upload &amp; payment in one place
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 text-gold" />
                  Access AI tools, LLB tutoring notes &amp; certificates
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 text-gold" />
                  Secure POPIA-compliant storage — your data stays yours
                </li>
              </ul>
            )}
            <p className="mt-4 text-center text-[11px] text-muted-foreground">
              By continuing you agree to our{" "}
              <Link to="/terms" className="underline hover:text-navy">terms</Link> and{" "}
              <Link to="/privacy" className="underline hover:text-navy">privacy policy</Link>.
            </p>
          </>
        )}
        <p className="mt-4 text-center text-xs text-muted-foreground">
          <Link to="/" className="hover:text-navy">← Back to site</Link>
        </p>
      </div>
    </div>
  );
}
